import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase, clearSupabaseAuthStorage, isInvalidRefreshTokenError } from '../../services/supabaseClient';
import { canAccessRoute } from '../../constants/routePermissions';
import { AdminRole } from '../../constants/roles';

interface AdminAuthGuardProps {
  children: React.ReactNode;
}

type AuthState = 'checking' | 'no_session' | 'non_admin' | 'authorized';

// ── 快取常數 ──────────────────────────────────────────────
const CACHE_KEY = 'admin_profile_cache';
const CACHE_TTL = 30 * 60 * 1000; // 30 分鐘，避免 admin route remount 時反覆查詢 admin_profiles
/** getSession timeout (ms) */
const GET_SESSION_TIMEOUT = 9000;
const AUTH_DEBUG = import.meta.env.DEV || import.meta.env.VITE_AUTH_DEBUG === 'true';

function authLog(...args: Parameters<typeof console.log>): void {
  if (AUTH_DEBUG) console.log(...args);
}

function authTime(label: string): void {
  if (AUTH_DEBUG) console.time(label);
}

function authTimeEnd(label: string): void {
  if (AUTH_DEBUG) console.timeEnd(label);
}

interface AdminProfileCache {
  auth_user_id: string;
  admin_id: string;
  role: string;
  status: string;
  assigned_location_id: string | null;
  name: string;
  email: string;
  cached_at: number;
}

interface AdminProfileRow {
  id: string;
  name: string;
  role: string;
  email: string;
  status?: string;
  assigned_location_id?: string | null;
}

interface AdminProfileLookupResult {
  data: AdminProfileRow | null;
  error: unknown;
}

let adminProfileLookupUid: string | null = null;
let adminProfileLookupPromise: Promise<AdminProfileLookupResult> | null = null;

function getAdminProfile(uid: string): Promise<AdminProfileLookupResult> {
  if (adminProfileLookupPromise && adminProfileLookupUid === uid) {
    authLog('[AdminAuthGuard] reusing in-flight admin_profiles query');
    return adminProfileLookupPromise;
  }

  adminProfileLookupUid = uid;
  adminProfileLookupPromise = Promise.resolve(
    supabase
      .from('admin_profiles')
      .select('id, name, role, email, status, assigned_location_id')
      .eq('auth_user_id', uid)
      .eq('status', 'active')
      .maybeSingle()
  )
    .then(({ data, error }) => ({ data: data as AdminProfileRow | null, error }))
    .finally(() => {
      adminProfileLookupUid = null;
      adminProfileLookupPromise = null;
    });

  return adminProfileLookupPromise;
}

/** 讀取快取；parse 失敗或欄位缺失時回傳 null */
function readAdminCache(): AdminProfileCache | null {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AdminProfileCache;
    // 欄位完整性檢查
    if (
      !parsed.auth_user_id ||
      !parsed.admin_id ||
      !parsed.role ||
      !parsed.status ||
      !parsed.cached_at
    ) {
      sessionStorage.removeItem(CACHE_KEY);
      return null;
    }
    return parsed;
  } catch {
    sessionStorage.removeItem(CACHE_KEY);
    return null;
  }
}

/** 將 admin profile 寫入快取 */
function writeAdminCache(params: {
  auth_user_id: string;
  admin_id: string;
  role: string;
  status: string;
  assigned_location_id: string | null;
  name: string;
  email: string;
}): void {
  const payload: AdminProfileCache = {
    ...params,
    cached_at: Date.now(),
  };
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(payload));
  } catch {
    // sessionStorage 寫入失敗（例如 private mode quota）→ 靜默忽略
  }
}

/** 清除快取 */
export function clearAdminProfileCache(): void {
  sessionStorage.removeItem(CACHE_KEY);
}

export function AdminAuthGuard({ children }: AdminAuthGuardProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [authState, setAuthState] = useState<AuthState>('checking');
  const [countdown, setCountdown] = useState(3);

  useEffect(() => {
    let isCurrent = true;
    checkAdminAuth(location.pathname, () => isCurrent);
    return () => {
      isCurrent = false;
    };
  }, [location.pathname]);

  useEffect(() => {
    if (authState !== 'non_admin') return;
    if (countdown <= 0) {
      navigate('/account', { replace: true });
      return;
    }
    const timer = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [authState, countdown, navigate]);

  const checkAdminAuth = async (routePath: string, isCurrent: () => boolean) => {
    authTime('[PERF] A AdminAuthGuard 整體驗證');
    try {
      // ── Step 1：取得 Session（含 timeout） ─────────────────
      authTime('[PERF] A1 getSession()');
      const sessionResult = await Promise.race([
        supabase.auth.getSession(),
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('getSession_timeout')), GET_SESSION_TIMEOUT)
        ),
      ]);
      authTimeEnd('[PERF] A1 getSession()');

      if (!isCurrent()) {
        authTimeEnd('[PERF] A AdminAuthGuard 整體驗證');
        return;
      }

      const { data: { session }, error: sessionError } = sessionResult;

      if (sessionError || !session?.user) {
        if (sessionError) {
          if (isInvalidRefreshTokenError(sessionError)) {
            console.warn('[AdminAuthGuard] invalid refresh token – clearing storage.');
            clearSupabaseAuthStorage();
          } else if (sessionError.message?.includes('getSession_timeout')) {
            console.warn('[AdminAuthGuard] getSession timeout – clearing storage.');
            clearSupabaseAuthStorage();
          } else {
            authLog('[AdminAuthGuard] no_session – getSession error:', sessionError.message);
          }
        } else {
          authLog('[AdminAuthGuard] no_session – no active session');
        }
        clearAdminLocals();
        clearAdminProfileCache();
        setAuthState('no_session');
        navigate('/admin/login?auth=recovered', { replace: true });
        authTimeEnd('[PERF] A AdminAuthGuard 整體驗證');
        return;
      }

      const uid = session.user.id;

      // ── Step 2：嘗試快取命中 ──────────────────────────────
      const cache = readAdminCache();
      const now = Date.now();
      const isCacheValid =
        cache !== null &&
        cache.auth_user_id === uid &&
        cache.status === 'active' &&
        now - cache.cached_at < CACHE_TTL;

      if (isCacheValid && cache) {
        // ✅ CACHE HIT
        const hitMs = now - cache.cached_at;
        authLog(
          `[PERF] A AdminAuthGuard CACHE HIT — age: ${hitMs}ms / ttl: ${CACHE_TTL}ms / role: ${cache.role}`
        );

        // 路由權限檢查（用快取 role 即可）
        const hasRouteAccess = canAccessRoute(cache.role as AdminRole, routePath);
        if (!hasRouteAccess) {
          console.warn('[AdminAuthGuard] cache_hit but route denied:', {
            role: cache.role,
            path: routePath,
          });
          writeAdminLocalsFromCache(cache);
          navigate('/admin/dashboard', { replace: true });
          authTimeEnd('[PERF] A AdminAuthGuard 整體驗證');
          return;
        }

        writeAdminLocalsFromCache(cache);
        setAuthState('authorized');
        authTimeEnd('[PERF] A AdminAuthGuard 整體驗證');
        return;
      }

      // ── Step 3（CACHE MISS）：查 admin_profiles ────────────
      if (cache !== null) {
        if (cache.auth_user_id !== uid) {
          authLog('[AdminAuthGuard] cache discarded – uid changed');
        } else {
          const ageMs = now - cache.cached_at;
          authLog(`[AdminAuthGuard] cache expired – age: ${ageMs}ms`);
        }
        clearAdminProfileCache();
      }

      authLog('[AdminAuthGuard] CACHE MISS – querying admin_profiles. uid:', uid);
      authTime('[PERF] A2 admin_profiles query');
      const { data: adminData, error: adminError } = await getAdminProfile(uid);
      authTimeEnd('[PERF] A2 admin_profiles query');

      if (!isCurrent()) {
        authTimeEnd('[PERF] A AdminAuthGuard 整體驗證');
        return;
      }

      if (adminError) {
        console.error('[AdminAuthGuard] admin_profiles query error:', adminError);
        clearAdminLocals();
        setAuthState('no_session');
        navigate('/admin/login?auth=recovered', { replace: true });
        authTimeEnd('[PERF] A AdminAuthGuard 整體驗證');
        return;
      }

      if (!adminData) {
        console.warn('[AdminAuthGuard] non_admin_user – uid:', uid, '/ email:', session.user.email);
        clearAdminLocals();
        setAuthState('non_admin');
        authTimeEnd('[PERF] A AdminAuthGuard 整體驗證');
        return;
      }

      // ── Step 4：路由權限檢查 ──────────────────────────────
      const hasRouteAccess = canAccessRoute(adminData.role as AdminRole, routePath);
      if (!hasRouteAccess) {
        console.warn('[AdminAuthGuard] admin_verified but route denied:', {
          role: adminData.role,
          path: routePath,
        });
        writeAdminLocals(adminData);
        writeAdminCache({
          auth_user_id: uid,
          admin_id: adminData.id,
          role: adminData.role,
          status: adminData.status ?? 'active',
          assigned_location_id: adminData.assigned_location_id ?? null,
          name: adminData.name,
          email: adminData.email,
        });
        navigate('/admin/dashboard', { replace: true });
        authTimeEnd('[PERF] A AdminAuthGuard 整體驗證');
        return;
      }

      // ── Step 5：全部通過，寫快取並授權 ────────────────────
      authLog('[AdminAuthGuard] admin_verified – role:', adminData.role, '/ id:', adminData.id);
      writeAdminLocals(adminData);
      writeAdminCache({
        auth_user_id: uid,
        admin_id: adminData.id,
        role: adminData.role,
        status: adminData.status ?? 'active',
        assigned_location_id: adminData.assigned_location_id ?? null,
        name: adminData.name,
        email: adminData.email,
      });
      setAuthState('authorized');
      authTimeEnd('[PERF] A AdminAuthGuard 整體驗證');

    } catch (error) {
      if (!isCurrent()) {
        authTimeEnd('[PERF] A AdminAuthGuard 整體驗證');
        return;
      }
      console.error('[AdminAuthGuard] unexpected error:', error);
      clearAdminLocals();
      clearAdminProfileCache();
      setAuthState('no_session');
      navigate('/admin/login?auth=recovered', { replace: true });
      authTimeEnd('[PERF] A AdminAuthGuard 整體驗證');
    }
  };

  const writeAdminLocals = (adminData: {
    id: string; name: string; role: string; email: string;
    status?: string; assigned_location_id?: string | null;
  }) => {
    localStorage.setItem('adminToken', 'verified');
    localStorage.setItem('adminId', adminData.id);
    localStorage.setItem('adminName', adminData.name);
    localStorage.setItem('adminRole', adminData.role);
    localStorage.setItem('adminEmail', adminData.email);
    if (adminData.assigned_location_id) {
      localStorage.setItem('adminLocationId', adminData.assigned_location_id);
    } else {
      localStorage.removeItem('adminLocationId');
    }
  };

  const writeAdminLocalsFromCache = (cache: AdminProfileCache) => {
    localStorage.setItem('adminToken', 'verified');
    localStorage.setItem('adminId', cache.admin_id);
    localStorage.setItem('adminName', cache.name);
    localStorage.setItem('adminRole', cache.role);
    localStorage.setItem('adminEmail', cache.email);
    if (cache.assigned_location_id) {
      localStorage.setItem('adminLocationId', cache.assigned_location_id);
    } else {
      localStorage.removeItem('adminLocationId');
    }
  };

  const clearAdminLocals = () => {
    ['adminToken', 'adminId', 'adminName', 'adminRole', 'adminEmail', 'adminLocationId']
      .forEach(k => localStorage.removeItem(k));
  };

  if (authState === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-gray-200 border-t-gray-900 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 text-sm">驗證權限中...</p>
        </div>
      </div>
    );
  }

  if (authState === 'non_admin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-6">
        <div className="max-w-md w-full bg-white rounded-2xl p-10 text-center">
          <div className="w-16 h-16 flex items-center justify-center bg-amber-50 rounded-full mx-auto mb-5">
            <i className="ri-shield-cross-line text-amber-500 text-3xl"></i>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">此帳號沒有後台權限</h2>
          <p className="text-gray-500 text-sm mb-6">
            您已登入的帳號並非管理員，無法進入後台系統。<br />
            {countdown} 秒後將自動帶您回到前台頁面。
          </p>
          <div className="flex flex-col gap-3">
            <button
              onClick={() => navigate('/account', { replace: true })}
              className="w-full bg-gray-900 text-white py-2.5 rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium cursor-pointer whitespace-nowrap"
            >
              立即回到前台
            </button>
            <button
              onClick={() => navigate('/admin/login', { replace: true })}
              className="w-full bg-gray-100 text-gray-700 py-2.5 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium cursor-pointer whitespace-nowrap"
            >
              使用管理員帳號登入
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (authState === 'no_session') {
    return null;
  }

  return <>{children}</>;
}